import { Badge, Tooltip } from 'antd'
import { LucideIcon } from 'components/LucideIcon'
import { useStepNavigation } from 'features/libra/StepNavigationContext'
import isEqual from 'lodash/isEqual'
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { matchAnchors } from './anchorMatching'
import { useCommentLayoutOptional } from './CommentLayoutProvider'
import { useComments } from './CommentsProvider'
import { type CommentAnchor, getAnchorKey } from './commentTypes'

function useStableAnchor(anchor: CommentAnchor): CommentAnchor {
  const ref = useRef(anchor)
  if (!isEqual(ref.current, anchor)) {
    ref.current = anchor
  }
  return ref.current
}

interface CommentIndicatorProps {
  anchor: CommentAnchor
  children: ReactNode
}

export const CommentIndicator: FC<CommentIndicatorProps> = ({
  anchor: anchorProp,
  children,
}) => {
  const anchor = useStableAnchor(anchorProp)
  const anchorKey = getAnchorKey(anchor)

  const {
    getThreadsForAnchor,
    startComposing,
    toggleSidebar,
    focusedAnchor,
    focusThread,
    isSidebarVisible,
  } = useComments()

  // The review step (and any step flagged `disableCommenting`) hides the
  // "Add comment" affordance; existing threads stay viewable.
  const { activeStep } = useStepNavigation()
  const isCommentingDisabled = activeStep?.disableCommenting ?? false

  const layout = useCommentLayoutOptional()

  const threads = useMemo(
    () => getThreadsForAnchor(anchor),
    [getThreadsForAnchor, anchor]
  )
  const unresolvedThreads = useMemo(
    () => threads.filter((t) => !t.resolved),
    [threads]
  )
  const unresolvedCount = unresolvedThreads.length

  const isFocused =
    focusedAnchor !== null && matchAnchors(anchor, focusedAnchor)

  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isFocused && wrapperRef.current) {
      wrapperRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [isFocused])

  useEffect(() => {
    if (!layout) return
    const el = wrapperRef.current
    layout.registerAnchorElement(anchorKey, el)
    return () => layout.registerAnchorElement(anchorKey, null)
  }, [layout, anchorKey])

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (unresolvedThreads.length > 0) {
        focusThread(unresolvedThreads[0].entryId)
      } else {
        startComposing(anchor)
        if (!isSidebarVisible) toggleSidebar()
      }
    },
    [
      unresolvedThreads,
      focusThread,
      anchor,
      startComposing,
      toggleSidebar,
      isSidebarVisible,
    ]
  )

  return (
    <Wrapper ref={wrapperRef} $isFocused={isFocused}>
      <ChildrenWrapper>{children}</ChildrenWrapper>
      <IconColumn>
        {unresolvedCount > 0 ? (
          <Tooltip
            title={`${unresolvedCount} unresolved comment${unresolvedCount > 1 ? 's' : ''}`}>
            <Badge count={unresolvedCount} size='small' offset={[0, 0]}>
              <CommentButton onClick={handleClick}>
                <LucideIcon name='MessageSquareText' size={13} />
              </CommentButton>
            </Badge>
          </Tooltip>
        ) : (
          !isCommentingDisabled && (
            <HoverOnlyButton>
              <Tooltip title='Add comment'>
                <CommentButton onClick={handleClick}>
                  <LucideIcon name='MessageSquareText' size={13} />
                </CommentButton>
              </Tooltip>
            </HoverOnlyButton>
          )
        )}
      </IconColumn>
    </Wrapper>
  )
}

const Wrapper = styled.div<{ $isFocused: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: box-shadow 0.2s ease;
  border-radius: 4px;

  ${({ $isFocused, theme }) =>
    $isFocused &&
    `
    box-shadow: 0 0 0 2px ${theme.forecast.highlight};
  `}
`

const ChildrenWrapper = styled.div`
  flex: 1;
  min-width: 0;
`

const IconColumn = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
`

const HoverOnlyButton = styled.div`
  opacity: 0;
  transition: opacity 0.15s ease;

  ${Wrapper}:hover & {
    opacity: 1;
  }
`

const CommentButton = styled.span`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: ${({ theme }) => theme.forecast.background1};
  box-shadow: ${({ theme }) => theme.forecast.boxShadow} 0 1px 3px;
  color: ${({ theme }) => theme.forecast.defaultText};

  &:hover {
    background: ${({ theme }) => theme.forecast.blueGlassBackground};
  }
`
